import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Form, message, Radio, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { BOAT_TRADER_SOURCE_ID, LEAD_SOURCE } from '../../constants';
import LoanLayout from '../LoanLayout';
import { getNextStep, gotoNextStep } from '../../nav';
import useHistory from '../../hooks/useHistory';
import { useLoan } from '../../stores/store';
import { useUser } from '../../hooks/useUser';
import AppFormItem from '../components/AppFormItem';
import styles from './PurposeStep.module.scss';
import {
  FinanceDetails,
  Loan,
  LoanPurpose,
  LoanType,
  Source,
  LeadOrigin,
  Prequal,
  KameleoonExperiment as Experiment,
  ValidUTMUrlParams,
  VALID_MARKETING_PARAMS,
  VALID_UTM_PARAMS,
  ValidLoanMarketingUrlParams,
  getEngineCountEntry,
  SellerType,
} from '../../schema';
import {
  fetchUnfinishedLoan,
  pushToDataLayer,
  betterParseInt,
  getCookie,
  formatEpochDate,
  sanitizeRate,
  sendCustomEventToIterable,
  formatIterablePayload,
} from '../../utils';
import useConfig from '../../stores/config';
import Loading from '../Loading';
import { camelCaseMarketingParam } from '../../utils/prequalHelper';
import { getNumericQueryParam } from '../../utils/queryParamHelper';
import { isValidLoanPurpose } from '../../utils/urls';

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

const parseBoatInfo = (info: string | null) => {
  try {
    if (info) {
      return JSON.parse(decodeURIComponent(info));
    }
  } catch (e) {
    console.log(e);
  }
  return null;
};

type Props = {};

const loadPrequalData = (
  isLendAPIFlow: boolean,
  isFromPrequal: boolean,
  prequalImtId: string | null,
  boatImtId?: string
): Partial<Prequal> | null => {
  if (!isLendAPIFlow && !isFromPrequal) {
    return null;
  }

  let imtId: string;

  // For LendAPI flow, use the saved imtId from the prequal session
  if (isLendAPIFlow) {
    const savedLendApiImtId = localStorage.getItem('current-lendapi-prequal-imtid');
    imtId = savedLendApiImtId || 'NONBDP';
  } else {
    // For internal prequal flow, use URL params as before
    imtId = prequalImtId || boatImtId || 'NONBDP';
  }

  const storageKey = `prequal-${imtId}`;
  const storedPrequal = localStorage.getItem(storageKey);

  if (storedPrequal) {
    const parsedPrequal = JSON.parse(storedPrequal) as Partial<Prequal>;
    if (Object.keys(parsedPrequal).length === 0) {
      return null;
    }
    return parsedPrequal;
  }

  return null;
};

const PurposeStep: React.FC<Props> = observer(() => {
  const history = useHistory();
  const user = useUser();
  const loanStore = useLoan();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  // Create enhanced params that combine URL params with LendAPI customParams (customParams override)
  const enhancedParams = useMemo(() => {
    const isLendAPIFlow = params.get('lendAPI') === 'true';

    if (!isLendAPIFlow) {
      return params;
    }

    // For LendAPI flow, merge URL params with customParams from stored prequal data
    const savedLendApiImtId = localStorage.getItem('current-lendapi-prequal-imtid');
    if (!savedLendApiImtId) {
      return params;
    }

    const storageKey = `prequal-${savedLendApiImtId}`;
    const storedPrequal = localStorage.getItem(storageKey);
    if (!storedPrequal) {
      return params;
    }

    try {
      const parsedPrequal = JSON.parse(storedPrequal);
      if (!parsedPrequal.customParams) {
        return params;
      }

      // Create enhanced params by combining URL params with decoded customParams
      const enhanced = new URLSearchParams(params.toString());
      const customParams = new URLSearchParams(parsedPrequal.customParams);

      // Add/override with customParams (customParams take precedence)
      customParams.forEach((value, key) => {
        enhanced.set(key, value);
      });

      return enhanced;
    } catch (e) {
      console.error('[PurposeStep] Error creating enhanced params:', e);
      return params;
    }
  }, [params]);

  // Extract parameters using enhancedParams for better LendAPI integration
  const isLendAPIFlow = params.get('lendAPI') === 'true';
  const purposeParam = params.get('purpose')?.toLowerCase();
  const repidParam = enhancedParams.get('repid');
  const canceledLogin = enhancedParams.get('canceledLogin');
  const isFromPrequal = enhancedParams.get('prequal') === 'true';
  const prequalImtId = enhancedParams.get('prequalImtId');
  const leadOrigin = enhancedParams.get('leadOrigin');
  const facebookClickId = (enhancedParams.get('fbclid') || '').substring(0, 100);
  const googleClickId = (enhancedParams.get('gclid') || '').substring(0, 100);
  const isValidPurpose = purposeParam ? isValidLoanPurpose(purposeParam) : false;
  const waitUntilLoanLoaded = params.get('loanId') && loanStore.status === 'init';
  const [isLoanLoading, setLoanLoading] = useState(false);
  const [redirected, setRedirected] = useState(false);
  const [autoPurposeTriggered, setAutoPurposeTriggered] = useState(false);

  // Initialize isAutoNavigating to prevent form flash
  // Use only regular params since purpose is guaranteed to be there
  // This avoids race condition with enhancedParams useMemo
  const rawUrlHasPurpose = !!params.get('purpose');
  const [isAutoNavigating, setIsAutoNavigating] = useState(rawUrlHasPurpose);
  const navigationInProgress = useRef(false);
  const loanCreationInProgress = useRef(false);
  const config = useConfig();

  const setPrefills = (loan: Loan, boatInfo: any): void => {
    if (boatInfo) {
      loan.collateral = {
        specificCollateralKnown: true,
        ...(boatInfo?.length ? { loa: boatInfo.length } : {}),
        ...(boatInfo?.year ? { year: boatInfo.year } : {}),
        ...(boatInfo?.type ? { type: boatInfo.type } : {}),
        ...(boatInfo?.make ? { manufacturer: boatInfo.make } : {}),
        ...(boatInfo?.model ? { modelNumber: boatInfo.model } : {}),
        ...(getEngineCountEntry(boatInfo?.engineCount)
          ? { engineCount: getEngineCountEntry(boatInfo.engineCount) }
          : {}),
        ...(boatInfo?.fuelType && boatInfo?.fuelType !== 'other'
          ? { fuel: boatInfo.fuelType.replace('gasoline', 'gas') }
          : {}),
        ...(boatInfo?.totalPower ? { horsepower: boatInfo.totalPower } : {}),
        ...(boatInfo?.engineMake ? { engineManufacturer: boatInfo.engineMake } : {}),
        ...(boatInfo?.imtId ? { imtId: boatInfo.imtId } : {}),
      };

      const purchasePrice = boatInfo?.price ?? undefined;
      loan.finance = {
        ...(purchasePrice ? { purchasePrice } : {}),
      } as FinanceDetails;

      if (boatInfo?.partyId) {
        loan.sellerPartyId = boatInfo.partyId;
      }

      if ((boatInfo?.sellerFinanceAdvantage ?? null) !== null) {
        loan.sellerFinanceAdvantage = boatInfo.sellerFinanceAdvantage;
      }

      if (boatInfo?.sellerName) {
        loan.sellerName = boatInfo.sellerName;
      }

      if (boatInfo?.sellerType) {
        loan.seller =
          boatInfo.sellerType === 'Broker/Dealer' ? SellerType.dealer : SellerType.private;
      }

      if (boatInfo.sellerType === 'Broker/Dealer' && boatInfo?.sellerEmail) {
        loan.sellerEmail = boatInfo.sellerEmail;
      }
    }

    if (googleClickId) {
      loan.googleClickId = googleClickId || loan?.googleClickId;
    }

    if (facebookClickId) {
      loan.facebookClickId = facebookClickId || loan?.facebookClickId;
    }

    // Handle both LendAPI and internal prequal flows using localStorage
    const loadedPrequal = loadPrequalData(
      isLendAPIFlow,
      isFromPrequal,
      prequalImtId,
      boatInfo?.imtId
    );

    if (loadedPrequal) {
      loan.prequal = loadedPrequal as Prequal;

      // Process prequal data for both flows
      const purchasePrice = boatInfo?.price ?? loadedPrequal.purchasePrice ?? undefined;

      loan.finance = {
        ...(purchasePrice ? { purchasePrice } : {}),
        ...(loadedPrequal.downPayment ? { downPayment: loadedPrequal.downPayment } : {}),
      } as FinanceDetails;

      if (loadedPrequal.shoppingSegment) {
        loan.shoppingSegment = loadedPrequal.shoppingSegment;
      }

      loan.borrower = {
        ...(loadedPrequal.firstName ? { firstName: loadedPrequal.firstName } : {}),
        ...(loadedPrequal.lastName ? { lastName: loadedPrequal.lastName } : {}),
        ...(loadedPrequal.phone ? { cellPhone: loadedPrequal.phone.replace(/[^0-9]/g, '') } : {}),
        ...(loadedPrequal.email ? { email: loadedPrequal.email } : {}),
        ...(loadedPrequal.address
          ? {
              currentAddress: {
                address1: loadedPrequal.address?.address1 ?? '',
                city: loadedPrequal.address?.city ?? '',
                country: loadedPrequal.address?.country ?? '',
                county: loadedPrequal.address?.county ?? '',
                state: loadedPrequal.address?.state ?? '',
                unitNumber: loadedPrequal.address?.unitNumber ?? '',
                zip: loadedPrequal.address?.zip ?? '',
              },
            }
          : {}),
      } as any;

      if (loadedPrequal.googleClickId) {
        loan.googleClickId = googleClickId || loadedPrequal?.googleClickId || loan?.googleClickId;
      }

      if (loadedPrequal.facebookClickId) {
        loan.facebookClickId =
          facebookClickId || loadedPrequal?.facebookClickId || loan?.facebookClickId;
      }
    }
  };

  const getDataFromConfig = ({
    sourceId,
    subSourceId,
  }: {
    sourceId: number | undefined;
    subSourceId?: number | undefined;
    rep?: any | undefined;
  }) => {
    if (!config) {
      return {};
    }
    let source;
    let subSource;
    let rep;
    try {
      if (repidParam) {
        rep = config.salesReps[repidParam];
      }
      if (sourceId !== undefined) {
        source = config.getSource(sourceId);
        if (subSourceId !== undefined) {
          subSource = config.getSubSource(source, subSourceId);
        }
      } else {
        source = config.getSourceFromLocation(window.location.href);
      }
    } catch (e) {
      console.error(e);
    }

    return {
      source,
      subSource,
      rep,
    };
  };

  let sourceId = betterParseInt(params.get('source'));
  let boatInfo = parseBoatInfo(enhancedParams.get('boatInfo'));
  const leadUUID = enhancedParams.get('leadUUID');

  // For LendAPI flow, extract boatInfo from the saved prequal data if URL boatInfo is missing
  if (isLendAPIFlow && !boatInfo) {
    const savedLendApiImtId = localStorage.getItem('current-lendapi-prequal-imtid');
    if (savedLendApiImtId) {
      const storageKey = `prequal-${savedLendApiImtId}`;
      const storedPrequal = localStorage.getItem(storageKey);
      if (storedPrequal) {
        try {
          const parsedPrequal = JSON.parse(storedPrequal);
          if (parsedPrequal.customParams) {
            const customParams = new URLSearchParams(parsedPrequal.customParams);
            const boatInfoParam = customParams.get('boatInfo');
            if (boatInfoParam) {
              boatInfo = JSON.parse(decodeURIComponent(boatInfoParam));
            }
          }
        } catch (e) {
          console.error('[PurposeStep] Error extracting boatInfo from LendAPI prequal:', e);
        }
      }
    }
  }

  if (loanStore && loanStore.loan) {
    sourceId = loanStore.loan.source;
    if (loanStore.loan.purpose === LoanPurpose.boat) {
      setPrefills(loanStore.loan, boatInfo);
    }
    if (leadUUID) {
      loanStore.loan.prequalLeadUUID = leadUUID;
    }
  }

  // Extract stable values to prevent MobX observer loops
  const stableLoanStoreId = loanStore.id;
  const stableLoanStoreStatus = loanStore.status;
  const stableLoanExists = !!loanStore?.loan;

  const onFinish = useCallback(
    async (purpose: LoanPurpose, thirdPartyDisclaimerSeen = false) => {
      try {
        if (!user) {
          // noinspection ExceptionCaughtLocallyJS
          throw new Error('missing user');
        }

        if (!loanStore.loan) {
          // Prevent double loan creation during rapid re-renders
          if (loanCreationInProgress.current) {
            console.warn(
              '[PurposeStep] Loan creation already in progress, skipping duplicate creation'
            );
            return;
          }
          loanCreationInProgress.current = true;

          // create a new app
          const now = Date.now();

          const loan = {
            currentStep: 'type',
            userId: user.uid,
            createdAt: now,
            updatedAt: now,
            source: 0,
            keycode: (enhancedParams.get('keycode') || '').substring(0, 100),
            // TRIDENT-896: internal-referral attribution (rename of utm_* + new internal_position).
            internal: {
              internalSource:
                (enhancedParams.get('internal_source') || '').substring(0, 100) || null,
              internalMedium:
                (enhancedParams.get('internal_medium') || '').substring(0, 100) || null,
              internalCampaign:
                (enhancedParams.get('internal_campaign') || '').substring(0, 100) || null,
              internalContent:
                (enhancedParams.get('internal_content') || '').substring(0, 100) || null,
              internalTerm: (enhancedParams.get('internal_term') || '').substring(0, 100) || null,
              internalId: (enhancedParams.get('internal_id') || '').substring(0, 100) || null,
              internalName: (enhancedParams.get('internal_name') || '').substring(0, 100) || null,
              internalPosition:
                (enhancedParams.get('internal_position') || '').substring(0, 100) || null,
            },
            promocode: (enhancedParams.get('promocode') || '').substring(0, 100),
            promocodeEditable: !(enhancedParams.get('promocode') || '').substring(0, 100).length,
            member: (enhancedParams.get('member') || '').substring(0, 100),
            rateQuoteId: (enhancedParams.get('rqid') || '').substring(0, 100),
            kamExpVariant: (enhancedParams.get('kamExpVariant') || '').substring(0, 255),
            salesForceId: (enhancedParams.get('sfid') || '').substring(0, 100),
            googleClickId,
            facebookClickId,
            workflow: 'draft',
            showMemberField:
              (loanStore.theme?.showMemberField && !enhancedParams.get('member')) ?? false,
            advertisedRate: sanitizeRate(getCookie('advertisedRate') ?? null),
            thirdPartyDisclaimerSeen,
          } as unknown as Loan;

          if (purpose === LoanPurpose.boat) {
            setPrefills(loan, boatInfo);
          }
          if (leadUUID) {
            loan.prequalLeadUUID = leadUUID;
          }

          const subSourceId = betterParseInt(enhancedParams.get('subsource'));
          const { source, subSource, rep } = getDataFromConfig({
            sourceId,
            subSourceId,
          });

          if (subSource) {
            loan.subSource = subSource.id;
          }

          if (source) {
            loan.source = source.id;
          }

          if (rep) {
            loan.repCode = rep.code;
          }

          if (leadOrigin) {
            loan.leadOrigin =
              LeadOrigin[leadOrigin?.toLowerCase() as keyof typeof LeadOrigin] || LeadOrigin.none;
          } else {
            loan.leadOrigin = LeadOrigin.none;
          }

          // save google utm parameters from the url
          try {
            const utm: { [k: string]: string } = {};
            Array.from(enhancedParams.entries()).forEach(([name, value]) => {
              if (VALID_UTM_PARAMS.has(name as ValidUTMUrlParams)) {
                utm[name.substring(0, 15)] = value.substring(0, 50);
              }
            });
            loan.utm = utm;
          } catch (e) {
            console.error(e);
          }

          try {
            const marketing: { [k: string]: string | number | null } = {};
            Array.from(enhancedParams.entries()).forEach(([name, value]) => {
              if (VALID_MARKETING_PARAMS.has(name.toLowerCase() as ValidLoanMarketingUrlParams)) {
                const isParamWithNumericValue = name === 'adgroupid' || name === 'campaignid';
                const key = camelCaseMarketingParam(name);

                if (isParamWithNumericValue) {
                  marketing[key] = getNumericQueryParam({ value, param: name });
                } else {
                  marketing[key] = value;
                }
              }
            });
            loan.marketing = marketing;
          } catch (e) {
            console.error(e);
          }

          let loanType = enhancedParams.get('type');
          if (loanType) {
            loanType = loanType.toLocaleLowerCase();
            if (Object.keys(LoanType).includes(loanType)) {
              loan.loanType = loanType as LoanType;
            }
          }

          if (subSource && (subSource.isReferral || subSource.products.includes(purpose))) {
            loan.subSource = subSource.id;
            loan.subSourceLabel = subSource.name;
          }

          const newLoanId = await loanStore.createLoan(loan);
          // Reset the flag after successful creation
          loanCreationInProgress.current = false;

          if (loan?.source === BOAT_TRADER_SOURCE_ID) {
            localStorage.setItem('loanId', newLoanId);
          }

          if (isFromPrequal || isLendAPIFlow) {
            const loadedPrequal = loadPrequalData(isLendAPIFlow, isFromPrequal, prequalImtId);

            if (loadedPrequal) {
              const castedPrequal = loadedPrequal as Prequal;
              loanStore.setValue('prequalLoanId', loadedPrequal.prequalApplicationId);
              const leadSource =
                loadedPrequal?.source !== undefined
                  ? LEAD_SOURCE.get(loadedPrequal?.source) ?? 'Other'
                  : 'Other';
              sendCustomEventToIterable({
                // TODO: ENSURE THIS WORKS FOR BOTH PREQUAL TYPES
                email: loadedPrequal.email as string,
                eventName: 'FullAppStarted',
                eventData: formatIterablePayload(castedPrequal, {
                  applicationStatus: loadedPrequal.fullApplicationStatus,
                  startedAt: loadedPrequal.fullApplicationStartedAt
                    ? formatEpochDate(loadedPrequal.fullApplicationStartedAt)
                    : undefined,
                  applicationId: loan?.id,
                  source: leadSource,
                  prequalId: loadedPrequal.prequalApplicationId,
                  loanPurpose: purpose,
                  loanType: loan?.loanType,
                  ...(castedPrequal.imtId === 'NONBDP' && { boatImtId: null }),
                }),
              });
            }
          }
        }

        // in case we went back to type
        loanStore.navDirection = 1;
        const nextStep = getNextStep();
        loanStore.setValue('purpose', purpose);
        await loanStore.syncDoc(nextStep);

        const vehicleType = purpose.toLowerCase();

        pushToDataLayer('e_applicationAction', {
          application_category: 'financing application',
          application_action: 'choose vehicle type',
          application_label: vehicleType,
          loan_purpose: vehicleType,
        });
        gotoNextStep(history, nextStep);

        // Reset navigation flag on successful completion
        navigationInProgress.current = false;
      } catch (e: any) {
        // Reset navigation and loan creation flags on error to allow retry
        navigationInProgress.current = false;
        loanCreationInProgress.current = false;
        setIsAutoNavigating(false); // Also clear loading state on error

        message.error(`Save failed: ${e.message ?? 'unknown error'}`);
        console.error('failed', e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      purposeParam,
      user,
      stableLoanStoreId,
      stableLoanStoreStatus,
      stableLoanExists,
      canceledLogin,
      redirected,
      isLoanLoading,
      isValidPurpose,
      history,
      params,
      setIsAutoNavigating,
    ]
  );

  const handlePurposeClick = (purpose: LoanPurpose) => {
    onFinish(purpose, true);
  };

  useEffect(() => {
    if (!localStorage.getItem('fullUrl')) {
      localStorage.setItem('fullUrl', window.location.href);
    }
  }, []);

  // Handle loan initialization and redirection logic - SEPARATED from MobX reactions
  const goToUnfinishedApp = useCallback(
    (loanId: string, step: string) => {
      const refinedParams = new URLSearchParams(params.toString());
      refinedParams.delete('loanId');
      refinedParams.delete('currentStep');

      history.push(`/${loanId}/${step}?${refinedParams}`);
      setRedirected(true);
    },
    [history, params]
  );

  // Handle loan loading without MobX mutations in effects
  const initializeLoanIfNeeded = useCallback(() => {
    // Use regular params instead of enhancedParams to avoid timing issues
    const localPrequalImtId = params.get('prequalImtId');
    const localIsLendAPIFlow = params.get('lendAPI') === 'true';
    const currentUrlLoanId = params.get('loanId'); // Check if URL already has a loan ID

    // Only skip unfinished loan loading for initial prequal transitions (no loan ID in URL yet)
    // If URL already has a loan ID, we should load that loan normally to prevent creating duplicates
    if ((localPrequalImtId || localIsLendAPIFlow) && !currentUrlLoanId) {
      return { unfinishedLoan: null, loanId: null };
    }

    const unfinishedLoan = fetchUnfinishedLoan();
    const loanId = canceledLogin ? null : unfinishedLoan?.loanId;

    // Only load the loan if we have a valid loanId and it's different from current
    if (loanId && loanId !== stableLoanStoreId) {
      // Trigger loan loading without being in a React effect
      Promise.resolve().then(() => {
        loanStore.setLoanId(loanId);
        setLoanLoading(true);
      });
    }

    return { unfinishedLoan, loanId };
  }, [canceledLogin, stableLoanStoreId, loanStore, params]);

  // Handle automatic purpose selection for valid URL parameters
  const handleAutoPurposeSelection = useCallback(() => {
    // If we already have a loan with a purpose, clear the auto-navigating flag
    if (stableLoanExists && loanStore.loan?.purpose) {
      if (isAutoNavigating) {
        setIsAutoNavigating(false);
      }
      return;
    }

    // If there's no valid purpose in URL, clear auto-navigating immediately
    if (!purposeParam || !isValidPurpose) {
      if (isAutoNavigating) {
        setIsAutoNavigating(false);
      }
      return;
    }

    // Only block for essential conditions, not user auth for direct URL access
    if (autoPurposeTriggered || navigationInProgress.current) {
      return;
    }

    // Use regular params instead of enhancedParams to avoid timing issues
    const localPrequalImtId = params.get('prequalImtId');
    const localIsLendAPIFlow = params.get('lendAPI') === 'true';

    // For direct URL access with purpose, proceed even if user isn't ready yet
    // User auth will be handled in onFinish when creating the loan
    if (!user) {
      // Wait for user auth for all flows (anonymous user for direct, authenticated for prequal/LendAPI)
      return;
    }
    const currentUrlLoanId = params.get('loanId'); // Check if URL already has a loan ID

    // Auto-select purpose for ANY valid purpose in URL (prequal, LendAPI, or direct access)
    // Only skip if URL already has a loan ID, let normal flow handle it
    // For any flow with purpose parameters, allow auto-navigation even if stable loan exists
    // (URL parameters represent explicit user intent to start new application)
    const shouldAllowDespiteExistingLoan = stableLoanExists && !!purposeParam;

    if (
      !currentUrlLoanId &&
      (!stableLoanExists || shouldAllowDespiteExistingLoan) &&
      !isLoanLoading
    ) {
      const isDirectAccess = !localPrequalImtId && !localIsLendAPIFlow;

      setAutoPurposeTriggered(true); // Prevent multiple executions
      navigationInProgress.current = true; // Prevent race conditions

      const purpose = purposeParam as 'rv' | 'boat' | 'aircraft';

      // Clear purpose parameters from URL to prevent re-triggering after navigation
      // BUT only for prequal/LendAPI flows - keep URL intact for direct access
      if (!isDirectAccess) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('purpose');
        window.history.replaceState({}, '', currentUrl.toString());
      }

      onFinish(LoanPurpose[purpose] as LoanPurpose);
      // Keep isAutoNavigating true during navigation - it will be cleared by successful navigation
      return;
    }

    const unfinishedLoan = fetchUnfinishedLoan();
    const loanId = canceledLogin ? null : unfinishedLoan?.loanId;
    const currentStep = unfinishedLoan?.currentStep;
    const validUnfinishedLoanValues = loanId && currentStep && currentStep !== 'type';

    // Only auto-select if no existing loan and not currently loading
    if (!validUnfinishedLoanValues && !stableLoanExists && !isLoanLoading) {
      setAutoPurposeTriggered(true); // Prevent multiple executions
      navigationInProgress.current = true; // Prevent race conditions
      const purpose = purposeParam as 'rv' | 'boat' | 'aircraft';
      onFinish(LoanPurpose[purpose] as LoanPurpose);
      // Keep isAutoNavigating true during navigation - it will be cleared by successful navigation
    }
    // Don't clear isAutoNavigating here - let it stay true until we're certain we won't auto-navigate
    // The flag will be cleared by other conditions or successful navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    purposeParam,
    user,
    isValidPurpose,
    autoPurposeTriggered,
    canceledLogin,
    stableLoanExists,
    isLoanLoading,
    isAutoNavigating,
    setIsAutoNavigating,
    onFinish,
    params,
    // Remove loanStore from dependencies to prevent infinite re-renders
    // Use stableLoanExists and other extracted stable values instead
  ]);

  // Handle redirection to unfinished applications
  const handleUnfinishedAppRedirection = useCallback(() => {
    // Skip unfinished app redirection if we're coming from prequal (either internal or LendAPI)
    if (params.get('prequalImtId') || params.get('lendAPI') === 'true') {
      return;
    }

    const unfinishedLoan = fetchUnfinishedLoan();
    const loanId = canceledLogin ? null : unfinishedLoan?.loanId;
    const currentStep = unfinishedLoan?.currentStep;
    const validUnfinishedLoanValues = loanId && currentStep && currentStep !== 'type';

    // Redirect if we have valid unfinished loan and store is done loading
    if (
      validUnfinishedLoanValues &&
      stableLoanStoreStatus === 'done' &&
      !redirected &&
      !canceledLogin
    ) {
      goToUnfinishedApp(loanId, currentStep);
    }
  }, [canceledLogin, stableLoanStoreStatus, redirected, goToUnfinishedApp, params]);

  // Must declare ALL hooks before any conditional returns
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => getDataFromConfig({ sourceId }), [sourceId, config]);

  // Initialize loan on component mount - NO MobX dependencies
  useEffect(() => {
    initializeLoanIfNeeded();
  }, [initializeLoanIfNeeded]); // Only run once on mount

  // Handle URL-based auto purpose selection - NO MobX dependencies
  useEffect(() => {
    // Skip if auto purpose has already been triggered to prevent infinite loops
    if (!autoPurposeTriggered) {
      handleAutoPurposeSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleAutoPurposeSelection, autoPurposeTriggered]);

  // Handle redirection when loan loading is complete - NO MobX dependencies
  useEffect(() => {
    handleUnfinishedAppRedirection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUnfinishedAppRedirection]);

  // Cleanup navigation and loan creation flags on component unmount
  useEffect(
    () => () => {
      // Reset flags when component unmounts to prevent stuck state
      navigationInProgress.current = false;
      loanCreationInProgress.current = false;
    },
    []
  );

  // IMPORTANT: All hooks must be declared BEFORE this conditional return
  // Determine if we should show loading or the purpose selection UI
  // For prequal flows with valid purposes: show loading to avoid UI flash
  // For non-prequal flows with valid purposes: show loading while auto-selecting
  // For everything else: show loading based on standard conditions
  const shouldShowLoading =
    !config ||
    !loanStore ||
    loanStore.status === 'pending' ||
    loanStore.status === 'not_found' ||
    isAutoNavigating || // Show loading immediately if we're going to auto-navigate
    (isValidPurpose && !stableLoanExists && !autoPurposeTriggered) || // Show loading for valid purposes while auto-selection hasn't been triggered
    waitUntilLoanLoaded;

  if (shouldShowLoading) {
    return <Loading />;
  }

  // Had to do this so that TS doesn't complain about Source being possibly undefined
  let source: Source = null as unknown as Source;

  if (data.source) {
    source = data.source;
  } else {
    source = config.getSource(0);
  }

  return (
    <LoanLayout>
      <h1 style={{ textAlign: 'center' }}>What are you financing?</h1>
      <Form {...formItemLayout} style={{ paddingBottom: '4rem' }}>
        <AppFormItem
          name="loanPurpose"
          className="button-form"
          hasFeedback={false}
          rules={[{ required: true, message: 'Please choose a button' }]}
          label=""
          wrapperCol={{ span: 24, offset: 0 }}
        >
          <Radio.Group className={styles.wide}>
            <Space size="large" direction="vertical" className={styles.wide}>
              {source.products.map((purpose: LoanPurpose) => (
                <Radio.Button
                  onClick={() => handlePurposeClick(purpose)}
                  value={purpose}
                  className={styles.purposeButton}
                  key={purpose}
                >
                  {purpose}
                </Radio.Button>
              ))}
            </Space>
          </Radio.Group>
        </AppFormItem>
      </Form>
    </LoanLayout>
  );
});

export default PurposeStep;
